// @ts-strict-ignore
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { antGrey, colorWithAlpha, runwayColors, rwColors, tagStyle } from '../styles/globalStyles';
import Icon, { SettingOutlined, CheckOutlined, CloseOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Automation, Integration, IntegrationCapability } from '@/types';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    displayTextForCategory,
    iconForAutomationType,
    iconForCapability,
    iconForCategory,
} from '@/helpers/displayHelpers';
import { categoryForAutomation, displayNameForCapability } from '@/helpers';
import { selectAppIntegrations, selectConnectedCapabilitiesForApp } from '@/selectors/selectors';
import { AppComponentProps } from '@/typescriptHelpers';

interface AutomationInfoProps extends AppComponentProps {
    refCallback: (node: HTMLDivElement) => void;
    automation: Automation;
    onShowAutomationSettings: (automationId: string) => void;
    onShowConnectIntegrations: (capabilityType: IntegrationCapability) => void;
}

export const AutomationInfoComponent: React.FC<AutomationInfoProps> = ({
    refCallback,
    appId,
    automation,
    onShowAutomationSettings,
    onShowConnectIntegrations,
}) => {
    const connectedCapabilities = useSelector((state: RootState) => selectConnectedCapabilitiesForApp(state, appId));
    const connectedIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));
    const [showCapabilityIcons, setShowCapabilityIcons] = React.useState(true);
    return (
        <div
            ref={refCallback}
            style={{
                padding: 24,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                backgroundColor: runwayColors.white,
                borderRadius: 12,
                borderStyle: 'solid',
                borderWidth: 1,
                borderColor: runwayColors.white,
                cursor: 'pointer',
            }}
            onClick={() => {
                setShowCapabilityIcons(!showCapabilityIcons);
            }}
        >
            <Space size={24} style={{ alignItems: 'flex-start' }}>
                <Icon
                    component={iconForAutomationType(automation.type)}
                    style={{ color: runwayColors.primary, fontSize: 28, paddingTop: '6px' }}
                />
                <Space size={18} direction="vertical" style={{ paddingRight: '150px' }}>
                    <Text style={{ fontSize: 20, fontWeight: 600, color: antGrey.grey8 }}>{automation.name}</Text>
                    <AutomationTagsComponent automation={automation} />
                    <Text style={{ fontSize: 16, color: antGrey.grey8 }}>{automation.description}</Text>
                    {showCapabilityIcons ?
                        <IntegrationCapabilityIcons
                            automation={automation}
                            appCapabilities={connectedCapabilities}
                            appIntegrations={connectedIntegrations}
                        />
                    :   <IntegrationCapabilityStatusModule
                            automation={automation}
                            appCapabilities={connectedCapabilities}
                            appIntegrations={connectedIntegrations}
                            onShowConnectIntegrations={onShowConnectIntegrations}
                        />
                    }
                </Space>
            </Space>
            <Space direction="vertical" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Tag
                    color={automation.enabled ? runwayColors.statusGreen : runwayColors.white}
                    style={{
                        ...tagStyle,
                        color: automation.enabled ? 'white' : antGrey.grey6,
                        borderColor: automation.enabled ? runwayColors.statusGreen : antGrey.grey6,
                    }}
                >
                    {automation.enabled ? 'ENABLED' : 'DISABLED'}
                </Tag>
                <Button
                    icon={<SettingOutlined />}
                    type="primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        onShowAutomationSettings(automation.id);
                    }}
                >
                    Configure
                </Button>
            </Space>
        </div>
    );
};

interface AutomationInfoSubComponentProps {
    automation: Automation;
}

interface IntegrationCapabilityIconProps extends AutomationInfoSubComponentProps {
    appCapabilities?: IntegrationCapability[];
    appIntegrations?: Integration[];
}

interface IntegrationCapabilityComponentProps extends IntegrationCapabilityIconProps {
    onShowConnectIntegrations: (capabilityType: IntegrationCapability) => void;
}

enum CapabilityStatus {
    Ready = 'Ready',
    NotInstalled = 'Not installed',
    Unreachable = 'Unreachable',
    Optional = 'Optional',
}

const colorForCapabilityStatus = (status: CapabilityStatus) => {
    switch (status) {
        case CapabilityStatus.Ready:
            return runwayColors.statusGreen;
        case CapabilityStatus.NotInstalled:
            return runwayColors.statusYellow;
        case CapabilityStatus.Unreachable:
            return runwayColors.statusRed;
        case CapabilityStatus.Optional:
            return antGrey.grey7;
    }
};

const capabilityStatus = (
    capability: IntegrationCapability,
    appCapabilities: IntegrationCapability[],
    appIntegrations: Integration[],
    requiredCapabilities: IntegrationCapability[][],
) => {
    if (appCapabilities && appCapabilities.includes(capability)) {
        for (const integration of appIntegrations) {
            if (integration.capabilities.includes(capability)) {
                if (integration.integrationError) {
                    return CapabilityStatus.Unreachable;
                }
                return CapabilityStatus.Ready;
            }
        }
    }
    if (requiredCapabilities.length > 0) {
        for (const capabilitySet of requiredCapabilities) {
            if (capabilitySet.includes(capability)) {
                for (const capability of capabilitySet) {
                    const capabilityIsConnected = appCapabilities?.includes(capability);
                    if (capabilityIsConnected) {
                        // If one of a set of optional capabilities is connected, if the others in the set are not connected they should display as 'Optional'
                        return CapabilityStatus.Optional;
                    }
                }
            }
        }
    }
    return CapabilityStatus.NotInstalled;
};

const IntegrationCapabilityIcons: React.FC<IntegrationCapabilityIconProps> = ({
    automation,
    appCapabilities,
    appIntegrations,
}) => {
    const allCapabilities = automation.requiredCapabilities?.reduce(
        (accumulator, capability) => accumulator.concat(capability),
        [],
    );
    return (
        <div style={{ marginTop: '-6px' }}>
            <Space size={12}>
                {allCapabilities &&
                    allCapabilities.length > 0 &&
                    allCapabilities.map((capability) => {
                        const status = capabilityStatus(
                            capability,
                            appCapabilities,
                            appIntegrations,
                            automation.requiredCapabilities,
                        );
                        return (
                            <Tooltip title={`${status}: ${displayNameForCapability(capability)}`} key={capability}>
                                <Icon
                                    key={capability}
                                    component={iconForCapability(capability)}
                                    style={{
                                        color: colorForCapabilityStatus(status),
                                        fontSize: 14,
                                        paddingTop: '-30px',
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
            </Space>
        </div>
    );
};

const IntegrationCapabilityStatusModule: React.FC<IntegrationCapabilityComponentProps> = ({
    automation,
    appCapabilities,
    appIntegrations,
    onShowConnectIntegrations,
}) => {
    const allCapabilities = automation.requiredCapabilities?.reduce(
        (accumulator, capability) => accumulator.concat(capability),
        [],
    );
    const ready: IntegrationCapability[] = [];
    const notInstalled: IntegrationCapability[] = [];
    const unreachable: IntegrationCapability[] = [];
    const optional: IntegrationCapability[] = [];
    if (allCapabilities) {
        for (const capability of allCapabilities) {
            const status = capabilityStatus(
                capability,
                appCapabilities,
                appIntegrations,
                automation.requiredCapabilities,
            );
            switch (status) {
                case CapabilityStatus.Ready:
                    ready.push(capability);
                    break;
                case CapabilityStatus.NotInstalled:
                    notInstalled.push(capability);
                    break;
                case CapabilityStatus.Unreachable:
                    unreachable.push(capability);
                    break;
                case CapabilityStatus.Optional:
                    optional.push(capability);
                    break;
            }
        }
    }
    return (
        <div style={{ gap: '16px', display: 'flex', flex: 1, flexDirection: 'column' }}>
            <IntegrationCapabilityStatusComponent status={CapabilityStatus.Ready} capabilities={ready} />
            <IntegrationCapabilityStatusComponent
                status={CapabilityStatus.NotInstalled}
                capabilities={notInstalled}
                requiredCapabilities={automation.requiredCapabilities}
                onShowConnectIntegrations={onShowConnectIntegrations}
            />
            <IntegrationCapabilityStatusComponent
                status={CapabilityStatus.Unreachable}
                capabilities={unreachable}
                onShowConnectIntegrations={onShowConnectIntegrations}
            />
            <IntegrationCapabilityStatusComponent
                status={CapabilityStatus.Optional}
                capabilities={optional}
                onShowConnectIntegrations={onShowConnectIntegrations}
            />
        </div>
    );
};

interface IntegrationCapabilityStatusComponentProps {
    capabilities: IntegrationCapability[];
    status: CapabilityStatus;
    requiredCapabilities?: IntegrationCapability[][];
    onShowConnectIntegrations?: (capabilityType: IntegrationCapability) => void;
}

const IntegrationCapabilityStatusComponent: React.FC<IntegrationCapabilityStatusComponentProps> = ({
    capabilities,
    status,
    requiredCapabilities,
    onShowConnectIntegrations,
}) => {
    const statusColor = colorForCapabilityStatus(status);

    let capabilitySets: IntegrationCapability[][] = [];
    if (requiredCapabilities && requiredCapabilities.length > 0 && status === CapabilityStatus.NotInstalled) {
        capabilitySets = requiredCapabilities
            .map((capabilitySet) => capabilitySet.filter((capability) => capabilities.includes(capability)))
            .filter((capabilitySet) => capabilitySet.length > 0);
    }

    return (
        <>
            {capabilities.length > 0 ?
                <div
                    style={{
                        borderRadius: '4px',
                        borderStyle: 'solid',
                        borderWidth: 1,
                        borderColor: statusColor,
                        backgroundColor: colorWithAlpha(statusColor, 0.05),
                        width: '100%',
                        paddingBottom: '4px',
                    }}
                >
                    <Space size={8} style={{ padding: '12px 16px' }} direction="vertical">
                        <Space size={8}>
                            {iconForCapabilityStatus(status)}
                            <Text style={{ fontSize: 14, fontWeight: 600, color: antGrey.grey8 }}>{status}</Text>
                        </Space>
                        {status === CapabilityStatus.Ready ?
                            <Space size={10}>
                                {capabilities.map((capability) => (
                                    <Tag
                                        key={capability}
                                        color={runwayColors.white}
                                        style={{
                                            color: statusColor,
                                            fontSize: 12,
                                            fontWeight: 400,
                                            borderRadius: '2px',
                                            borderColor: statusColor,
                                        }}
                                        icon={React.createElement(iconForCapability(capability))}
                                    >
                                        {displayNameForCapability(capability)}
                                    </Tag>
                                ))}
                            </Space>
                        : status === CapabilityStatus.NotInstalled && requiredCapabilities?.length > 0 ?
                            capabilitySets.map((capabilitySet) => (
                                <Space
                                    key={capabilitySets.indexOf(capabilitySet)}
                                    size={10}
                                    split={
                                        <Text
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: 400,
                                                color: antGrey.grey8,
                                                marginLeft: '-10px',
                                                marginRight: '10px',
                                            }}
                                        >
                                            OR
                                        </Text>
                                    }
                                    wrap
                                >
                                    {capabilitySet.map((capability) =>
                                        capabilityUIWithButton(capability, status, onShowConnectIntegrations),
                                    )}
                                </Space>
                            ))
                        :   <Space size={10} direction="vertical">
                                {capabilities.map((capability) =>
                                    capabilityUIWithButton(capability, status, onShowConnectIntegrations),
                                )}
                            </Space>
                        }
                    </Space>
                </div>
            :   null}
        </>
    );
};

const capabilityUIWithButton = (
    capability: IntegrationCapability,
    status: CapabilityStatus,
    onClick: (capabilityType: IntegrationCapability) => void,
) => {
    const statusColor = colorForCapabilityStatus(status);
    const integrationButtonText =
        status === CapabilityStatus.Unreachable ? 'Check integration settings' : 'Connect Now ->';
    return (
        <Space key={capability}>
            <Tag
                color={runwayColors.white}
                style={{
                    color: statusColor,
                    fontSize: 12,
                    fontWeight: 400,
                    borderRadius: '2px',
                    borderColor: statusColor,
                }}
                icon={React.createElement(iconForCapability(capability))}
            >
                {displayNameForCapability(capability)}
            </Tag>
            {status === CapabilityStatus.Unreachable ?
                <Text
                    style={{
                        color: antGrey.grey8,
                        fontSize: '12px',
                        fontWeight: 600,
                        marginLeft: '8px',
                        height: '20px',
                    }}
                >
                    {integrationButtonText}
                </Text>
            :   <Button
                    type="link"
                    style={{
                        color: runwayColors.primary,
                        fontSize: '12px',
                        fontWeight: 600,
                        marginLeft: '-8px',
                        height: '20px',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick(capability);
                    }}
                >
                    {integrationButtonText}
                </Button>
            }
        </Space>
    );
};

const iconForCapabilityStatus = (status: CapabilityStatus) => {
    switch (status) {
        case CapabilityStatus.Ready:
            return <CheckOutlined />;
        case CapabilityStatus.NotInstalled:
            return <CloseOutlined />;
        case CapabilityStatus.Unreachable:
            return <CloseOutlined />;
        case CapabilityStatus.Optional:
            return <ArrowRightOutlined />;
    }
};

const AutomationTagsComponent: React.FC<AutomationInfoSubComponentProps> = ({ automation }) => {
    const category = categoryForAutomation(automation.type);
    const categoryIcon = iconForCategory(category);
    const categoryTagText = displayTextForCategory(category);
    const isNew = automation.isNew;
    return (
        <Space size={12}>
            {category && (
                <Tag
                    icon={React.createElement(categoryIcon)}
                    color={antGrey.grey3}
                    style={{ color: antGrey.grey8, fontSize: 12, fontWeight: 400 }}
                >
                    {categoryTagText}
                </Tag>
            )}
            {isNew && (
                <Tag
                    color={colorWithAlpha(rwColors.primary, 0.1)}
                    style={{
                        color: runwayColors.primary,
                        fontSize: 12,
                        fontWeight: 400,
                        borderRadius: '2px',
                        borderColor: colorWithAlpha(rwColors.primary, 0.5),
                    }}
                >
                    New
                </Tag>
            )}
        </Space>
    );
};
